// Hand-rolled SVG grouped/stacked bar chart — no charting library, matching the app's existing
// hand-SVG convention (see components/dashboard/SLAChart.tsx).
import { useId } from 'react';

export interface BarSeries {
  label: string;
  color: string;
  data: (number | null)[];
  colors?: string[];
}

interface BarChartProps {
  labels: string[];
  series: BarSeries[];
  height?: number;
  yAxisLabel?: string;
  formatValue?: (v: number) => string;
  stacked?: boolean;
  showLegend?: boolean;
}

const W = 640;

export function BarChart({
  labels,
  series,
  height = 260,
  yAxisLabel,
  formatValue = (v) => v.toFixed(1),
  stacked = false,
  showLegend = true,
}: BarChartProps) {
  const uid = useId();
  const H = height;
  const padLeft = 44;
  const padBottom = 28;
  const padTop = 10;
  const padRight = 10;
  const plotW = W - padLeft - padRight;
  const plotH = H - padTop - padBottom;

  const maxValue = stacked
    ? Math.max(
        0,
        ...labels.map((_, i) => series.reduce((sum, s) => sum + (s.data[i] ?? 0), 0)),
      )
    : Math.max(0, ...series.flatMap((s) => s.data.map((v) => v ?? 0)));
  const niceMax = maxValue === 0 ? 1 : maxValue * 1.1;
  const ticks = 4;
  const tickValues = Array.from({ length: ticks + 1 }, (_, i) => (niceMax / ticks) * i);

  function yFor(v: number) {
    return padTop + plotH - (v / niceMax) * plotH;
  }

  if (labels.length === 0 || series.length === 0) {
    return <div className="text-sm text-slate-400 py-8 text-center">No data</div>;
  }

  const groupW = plotW / labels.length;
  const barGap = 2;
  const barW = stacked ? groupW * 0.6 : (groupW * 0.7) / series.length;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} role="img" aria-label={yAxisLabel}>
        {/* Gridlines + y-axis labels */}
        {tickValues.map((tv, i) => (
          <g key={i}>
            <line x1={padLeft} x2={W - padRight} y1={yFor(tv)} y2={yFor(tv)} stroke="#e2e8f0" strokeWidth={1} />
            <text x={padLeft - 6} y={yFor(tv) + 3} textAnchor="end" fontSize={9} fill="#94a3b8">
              {formatValue(tv)}
            </text>
          </g>
        ))}
        {/* Baseline */}
        <line x1={padLeft} x2={W - padRight} y1={yFor(0)} y2={yFor(0)} stroke="#cbd5e1" strokeWidth={1} />

        {/* Bars */}
        {labels.map((label, i) => {
          const groupX = padLeft + i * groupW;
          if (stacked) {
            let cumulative = 0;
            return (
              <g key={`${uid}-${i}`}>
                {series.map((s, si) => {
                  const v = s.data[i] ?? 0;
                  const y0 = yFor(cumulative);
                  const y1 = yFor(cumulative + v);
                  cumulative += v;
                  return (
                    <rect
                      key={si}
                      x={groupX + (groupW - barW) / 2}
                      y={y1}
                      width={barW}
                      height={Math.max(0, y0 - y1)}
                      fill={s.colors?.[i] ?? s.color}
                    >
                      <title>
                        {s.label}: {formatValue(v)}
                      </title>
                    </rect>
                  );
                })}
              </g>
            );
          }
          return (
            <g key={`${uid}-${i}`}>
              {series.map((s, si) => {
                const v = s.data[i];
                if (v == null) return null;
                const barX = groupX + (groupW - barW * series.length - barGap * (series.length - 1)) / 2 + si * (barW + barGap);
                const y = yFor(v);
                return (
                  <rect key={si} x={barX} y={y} width={barW} height={Math.max(0, yFor(0) - y)} fill={s.colors?.[i] ?? s.color} rx={2}>
                    <title>
                      {s.label}: {formatValue(v)}
                    </title>
                  </rect>
                );
              })}
            </g>
          );
        })}

        {/* X-axis labels */}
        {labels.map((label, i) => {
          const showEvery = labels.length > 16 ? Math.ceil(labels.length / 16) : 1;
          if (i % showEvery !== 0) return null;
          const x = padLeft + i * groupW + groupW / 2;
          return (
            <text
              key={i}
              x={x}
              y={H - padBottom + 14}
              textAnchor="middle"
              fontSize={9}
              fill="#94a3b8"
              transform={labels.length > 8 ? `rotate(-40 ${x} ${H - padBottom + 14})` : undefined}
            >
              {label}
            </text>
          );
        })}
      </svg>
      {showLegend && series.length > 1 && (
        <div className="flex flex-wrap gap-3 justify-center mt-1">
          {series.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
